import { Spinner, Toggle, Badge, Flashbar, Button, ProgressBar } from 'AWS-UI-Components-React';

import 'rsuite/dist/styles/rsuite-default.css';

import React from 'react';
import { accumulateProductLineById } from '../../../utils/product_line';
import { getJSClient } from '../../js_client';
import { COGNOS_BRIDGING_FACTORS, HCP_COOP_BRIDGING_FACTORS } from './bridging_constants';
import { ColumnGroup, Table, Column, HeaderCell, Cell } from 'rsuite-table';
import { formatNumber } from '../utils/formatting';
import _ from 'lodash';
import logger from '../../../utils/logging';
import '../../../styles/custom_rsuite_table.css';
import { extractErrorMessage } from '../../../utilities'
import { getShipMonthsFromTimeFrames } from '../../../utils/date-utils.jsx'

/**
 * Renders bridging report.
 */
export class BridgingReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      loading: true,
      displayInMillions: true,
      endRow: 0,
      totalRows: 0,
      expandAllRows: false,
      expandedRows: [],
      error: false, 
      errorMessage: ''
    };
    this.payload = props.payload;
    this.productLineMap = {};
    this.productLines = props.productLines;
  };

  componentDidMount = () => {
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.client = getJSClient();
    accumulateProductLineById(this.productLines, null, this.productLineMap);
    this.payload.timeFrame = getShipMonthsFromTimeFrames(this.payload.timeFrame).join();
    this.startFetchingBridgingFactors();
  };

  startFetchingBridgingFactors = () => {
    this.setState({ loading: true, totalRows: 0, endRow: 0, rows: [], expandedRows: [], error: false });
    this.client.getBridgingFactorsReport(this.onBridgingFactorsPull, 0, this.onFailedRequest, this.payload);
  }

  onBridgingFactorsPull = (data = {}, page) => {
    let loading = false;
    logger.info(`Fetched bridging factors for page: ${page}`);

    data.rows.forEach(item => { 
      item.productLine = (this.productLineMap[item.productLineId] || {}).name; 
      item['key'] = `${item.shipMonth}-${item.marketplaceId}-${item.productLineId}`
      Object.keys(item.cognosBridgingFactors).forEach(function(key) {
        item[key] = item.cognosBridgingFactors[key];
      });
    });

    if (data.endRow < data.totalRows - 1) {
      loading = true;
      this.client.getBridgingFactorsReport(this.onBridgingFactorsPull, page+1, this.onFailedRequest, this.payload);
    } 

    const { totalRows, endRow } = data;
    let rows = [...this.state.rows, ...data.rows];
    this.setState({ rows, loading, totalRows, endRow });

    if (!loading) {
      let { topLevelRows, rowMap } = this.createPLHierarchy(rows);
      this.calculateParentSums(topLevelRows);
      let topRowKeys = topLevelRows.map((value, _key) => {
        return value.key;
      });
      this.setState({ rows: topLevelRows, expandedRows: topRowKeys, rowMap });
    }
  }

  calculateParentSums = (rows = []) => {
    let calculateParentSums = this.calculateParentSums;
    rows.forEach(row => {
      if (row.children) {
        row.children.forEach(function(child) { 
          calculateParentSums([child]);
          Object.keys(COGNOS_BRIDGING_FACTORS).map(factor =>  {
            let key = COGNOS_BRIDGING_FACTORS[factor].key;
            row[key] = (row[key] || 0) + (child[key] || 0);
          }) 
        });
      }
    }) 
  }

  createPLHierarchy = (rows = []) => {
    let monthMarketplaceCombinations = new Map();
    let rowMap = new Map();
    let productLineMap = this.productLineMap;
    
    for(let row of rows) {
      monthMarketplaceCombinations.set(`${row.shipMonth}-${row.marketplaceId}`, {
        shipMonth: row.shipMonth,
        marketplaceId: row.marketplaceId
      })
      rowMap.set(row.key, row);
    }

    let parentMap = new Map();
    for (let [combo, value] of monthMarketplaceCombinations) {
      for (let productLineId of Object.keys(productLineMap).filter(id => productLineMap[id].children)) {
        let key = `${combo}-${productLineId}`
        let productLine = productLineMap[productLineId];
        rowMap.set(key, {
          key: key,
          productLineId: productLineId,
          productLine: productLine.name,
          shipMonth: value.shipMonth,
          marketplaceId: value.marketplaceId
        });
        parentMap.set(key, rowMap.get(key));
      }
    }

    parentMap.forEach(row => {
      let children = productLineMap[row.productLineId].children;
      let prefix = `${row.shipMonth}-${row.marketplaceId}`;
      row.children = [];
      if (children && children != null) {
        children.filter(item => item.id !== "-1").map(item => {
          let child = rowMap.get(`${prefix}-${item.id}`);
          if (child) {
            row.children.push(child);
          }
        })
      }
    })
    
    let topLevelRows = [];
    
    rowMap.forEach(value => {
      if (value.productLineId === 'ALL') {
        topLevelRows.push(value);
      }
    });
    
    return { topLevelRows, rowMap };
  };

  onFailedRequest = (err) => {
    this.setState({ error: true, loading: false, errorMessage: extractErrorMessage(err) });
  };

  onToggleMillions = (chosen) => {
    this.setState({ displayInMillions: chosen.detail.checked});
  };

  onExpandAllRows = (chosen) => {
    let expandedRows = [];
    if (chosen.detail.checked) {
      this.state.rowMap.forEach((value, key) => {
        if (value.children) {
          expandedRows.push(key);
        }
      });
    }
    this.setState({ expandedRows: expandedRows });
  };

  onExpandChange = (expanded, rowData) => {
    if (expanded) {
      this.setState({ expandedRows: [...this.state.expandedRows, rowData.key] });
    } else {
      this.setState({ expandedRows: this.state.expandedRows.filter((key) => key !== rowData.key) });
    }
  }

  MonetaryCell = ({ rowData, dataKey, ...props }) => (
    <Cell {...props}>
      <div style={{ marginRight:20, textAlign:'right' }}>{props.millions === "true" ? formatNumber(rowData[dataKey], 'monetary', true) : formatNumber(rowData[dataKey], 'monetary', false) }</div>
    </Cell>
  );

  render() {
    const { rows, loading, error, errorMessage, displayInMillions, endRow, totalRows, expandedRows, expandAllRows } = this.state;
    return (
        
        <div className="awsui-util-ph-xs">
          <div className="awsui-util-container">
          <div className="awsui-util-container-header">
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                 {
                   loading && <Badge color="blue"> <Spinner size="normal" /> {totalRows === 0 ? 'Loading' : `Loaded ${endRow} of ${totalRows} records` } </Badge>
                 }
                 {
                   error && <Flashbar items={[
                    {
                      header: `Failed to get bridging factors: ${errorMessage}`,
                      type: "error",
                      buttonText: "Retry",
                      buttonClick: () => this.startFetchingBridgingFactors(),
                      dismissible: true
                    }
                  ]} />
                 }
              </div>
              <div className="awsui-util-action-stripe-group">
                <div className="awsui-util-p-s">
                  <Toggle onChange={this.onToggleMillions} checked={displayInMillions}>Display in Millions</Toggle>
                </div>
                <div className="awsui-util-p-s">
                  <Toggle onChange={this.onExpandAllRows} checked={expandAllRows}>Expand All Rows</Toggle>
                </div>
              </div>
            </div>
          </div>
          
          <div style={{  padding: '0px' }}>
            <Table data={rows} height={700} affixHeader virtualized headerHeight={70} isTree expandedRowKeys={expandedRows} shouldUpdateScroll={false} onExpandChange={this.onExpandChange}>
              <Column width={100} fixed align="center">
                <HeaderCell verticalAlign='middle'>Marketplace</HeaderCell>
                <Cell dataKey='marketplaceId' />
              </Column>

              <Column width={100} align="center" fixed>
                <HeaderCell verticalAlign="middle">Month</HeaderCell>
                <Cell dataKey='shipMonth' />
              </Column>
              
              <Column width={ expandedRows.length > 0 ? 200 : 100 } resizable fixed treeCol>
                <HeaderCell verticalAlign="middle">Product Line</HeaderCell>
                <Cell dataKey='productLine' />
              </Column>

              <Column width={120} align="center">
                <HeaderCell verticalAlign="middle">Product Line Id</HeaderCell>
                <Cell dataKey='productLineId' />
              </Column>

              <ColumnGroup header="HCP COOP" align="center">
              {
                Object.keys(HCP_COOP_BRIDGING_FACTORS).map(factor => {
                  let title = HCP_COOP_BRIDGING_FACTORS[factor].title;
                  let key = HCP_COOP_BRIDGING_FACTORS[factor].key;
                  let width = title.length < 6 ? (displayInMillions ? 50 : 100) : title.length * 10;
                  return (
                    <Column key={key} width={width} resizable>
                      <HeaderCell style={{backgroundColor: 'ghostwhite'}}>{title}</HeaderCell>
                      <this.MonetaryCell align="right" dataKey={key} millions={displayInMillions.toString()} />
                    </Column>
                  )
                })
              }
              </ColumnGroup>

              <ColumnGroup header="Cognos" align="center">
              {
                Object.keys(COGNOS_BRIDGING_FACTORS).map(factor => {
                  let title = COGNOS_BRIDGING_FACTORS[factor].title;
                  let key = COGNOS_BRIDGING_FACTORS[factor].key;
                  let width = title.length < 6 ? (displayInMillions ? 50 : 100) : title.length * 10;
                  return (
                    <Column key={key} width={width} resizable>
                      <HeaderCell style={{backgroundColor: 'gainsboro'}}>{title}</HeaderCell>
                      <this.MonetaryCell align="right" dataKey={key} millions={displayInMillions.toString()} />
                    </Column>
                  )
                })
              }
              </ColumnGroup>

              <Column width={100} resizable>
                <HeaderCell verticalAlign="middle">Unexplained (%)</HeaderCell>
                <Cell dataKey='unExplainedPercent' />
              </Column>

              <Column width={120} align="center">
                <HeaderCell verticalAlign="middle">Unexplained</HeaderCell>
                <Cell dataKey='unExplained' />
              </Column>
            </Table>
          </div>
        </div>
      </div>
    );
  };
}



// WEBPACK FOOTER //
// ./src/components/reports/bridging/bridging_report.jsx